import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SeatsService } from '../../../services/seat-service/seats.service';
import { RootState } from '../../../state/state';
import { seatMapLookup, seatMapLookupComplete, staticSeatMapLookup } from './seat-map-v2.actions';
import { catchError, map, mergeMap, of, withLatestFrom } from 'rxjs';
import { SeatMapSelectors } from '../../seat-map/state/seat-map.selectors';
import { setCurrentSegmentHashId } from '../../seat-map/state/seat-map.actions';
import { SeatMapLookupStatus } from '../../../dtos/response/seat-map-lookup-response/seat-map-lookup-status';
import { ReservationServiceSelectors } from '../../../services/reservation-service/state/reservation-service.selectors';

@Injectable()
export class SeatMapEffects {
  private readonly actions$ = inject(Actions);
  private readonly seatsService = inject(SeatsService);
  private readonly store = inject<Store<RootState>>(Store);

  /**
   * Gets a seatmap for a flight on a reservation
   * Handles pre-reserved seats, passenger data, etc in post processing
   */
  seatMapLookup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(seatMapLookup),
      withLatestFrom(
        this.store.select(SeatMapSelectors.getCurrentSegmentHash),
        this.store.select(SeatMapSelectors.getCurrentSeatMapSegment)
      ),
      mergeMap(([action, currentSegmentHashId, currentSeatMapSegment]) => {
        if (!currentSegmentHashId) {
          this.store.dispatch(setCurrentSegmentHashId(currentSeatMapSegment?.hashId || null));
        }

        return this.seatsService.seatMapLookupV2(action.seatMapLookupRequest, action.allCabinsRequested, false).pipe(
          map((result) => {
            const passengers = this.store.selectSignal(ReservationServiceSelectors.getPassengersAndExtraSeatPassengers)();
            passengers.forEach((passenger) => {
              // Find the passenger's assigned seat using seatHashId
              if (passenger.originalSeats?.length) {
                passenger.originalSeats.forEach((seat) => {
                  // extract all the seats from the seatmaps
                  const allSeats = result.response?.seatMaps?.flatMap((seatMap) => seatMap.rows.flatMap((row) => row.seats)) || [];
                  const assignedSeat = allSeats.find((s) => s.hashId === seat.seatHashId);
                  if (assignedSeat) {
                    // Update the passengerHashId on the seat to link passenger to seat in the seatmap
                    assignedSeat.passengerHashId = passenger.hashId;
                  }
                });
              }
            });
            return seatMapLookupComplete(result);
          }),
          /**
           * When unknown errors happen from calling the service, instead of catching the error
           * inside outer stream (e.g., mergeMap, exhaustMap), catch error on inner stream, to keep the corresponding effect alive.
           * See NgRx effects error handling section for more details: https://v7.ngrx.io/guide/effects
           */
          catchError(() => {
            return of(
              seatMapLookupComplete({
                success: false,
                status: SeatMapLookupStatus.SYSTEM_FAILURE,
              })
            );
          })
        );
      })
    )
  );

  /**
   * Gets a static seatmap for a flight within a search result.
   * Handles pre-reserved seats, passenger data, etc. in post processing.
   */
  staticSeatMapLookup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(staticSeatMapLookup),
      mergeMap((action) => {
        return this.seatsService.seatMapLookupV2(action.seatMapLookupRequest, action.allCabinsRequested).pipe(
          map((result) => {
            return seatMapLookupComplete(result);
          }),
          /**
           * When unknown errors happen from calling the service, instead of catching the error
           * inside outer stream (e.g., mergeMap, exhaustMap), catch error on inner stream, to keep the corresponding effect alive.
           * See NgRx effects error handling section for more details: https://v7.ngrx.io/guide/effects
           */
          catchError(() => {
            return of(
              seatMapLookupComplete({
                success: false,
                status: SeatMapLookupStatus.SYSTEM_FAILURE,
              })
            );
          })
        );
      })
    )
  );
}
